import { DEFAULTS } from './defaults.ts';
import { GEM_NAMES, GEM_TYPE_COUNT, defaultGemType, type Board, type BoardCell, type BoardData, type Goal, type LevelParams, type Objectives, type Obstacle, type ObstacleData, type Pickup } from './types.ts';

const PARAM_KEYS: (keyof LevelParams)[] = [
  'spawnGapTicks',
  'totalCreatures',
  'onScreenCap',
  'mutateThreshold',
  'mutateDurationTicks',
  'creatureTicksPerCell',
  'turnSwapChance',
  'goldTicks',
  'bonusTicks',
];

export function validateParams(p: unknown, boardId: string): LevelParams {
  if (!p || typeof p !== 'object') throw new Error(`Board ${boardId}: missing params`);
  const obj = p as Record<string, unknown>;
  for (const k of PARAM_KEYS) {
    if (typeof obj[k] !== 'number' || Number.isNaN(obj[k])) {
      throw new Error(`Board ${boardId}: params.${k} must be a number`);
    }
  }
  if (obj.sackShadowTicks !== undefined && typeof obj.sackShadowTicks !== 'number') {
    throw new Error(`Board ${boardId}: params.sackShadowTicks must be a number`);
  }
  if (obj.frustrationCountsDirt !== undefined && typeof obj.frustrationCountsDirt !== 'boolean') {
    throw new Error(`Board ${boardId}: params.frustrationCountsDirt must be a boolean`);
  }
  if (obj.lanternStartCharges !== undefined && (typeof obj.lanternStartCharges !== 'number' || obj.lanternStartCharges < 0)) {
    throw new Error(`Board ${boardId}: params.lanternStartCharges must be a number >= 0`);
  }
  if (obj.gemsPerCharge !== undefined && (typeof obj.gemsPerCharge !== 'number' || obj.gemsPerCharge < 1)) {
    throw new Error(`Board ${boardId}: params.gemsPerCharge must be a number >= 1`);
  }
  const speed = obj.playerSpeedTicksPerCell as { h?: unknown; v?: unknown } | undefined;
  if (speed !== undefined && (typeof speed !== 'object' || typeof speed.h !== 'number' || typeof speed.v !== 'number')) {
    throw new Error(`Board ${boardId}: params.playerSpeedTicksPerCell must be { h: number, v: number }`);
  }
  const { flameRechargeTicks: _legacy, ...rest } = obj; // WP6: timed recharge removed; ignored if an old board still carries it
  void _legacy;
  return {
    ...rest,
    lanternStartCharges: (obj.lanternStartCharges as number | undefined) ?? DEFAULTS.lanternStartCharges,
    gemsPerCharge: (obj.gemsPerCharge as number | undefined) ?? DEFAULTS.lanternGemsPerCharge,
    sackShadowTicks: (obj.sackShadowTicks as number | undefined) ?? DEFAULTS.sackShadowTicks,
    frustrationCountsDirt: (obj.frustrationCountsDirt as boolean | undefined) ?? DEFAULTS.frustrationCountsDirt,
    playerSpeedTicksPerCell: speed ? { h: speed.h as number, v: speed.v as number } : { h: DEFAULTS.playerTicksPerCellH, v: DEFAULTS.playerTicksPerCellV },
  } as unknown as LevelParams;
}

/** Parse + validate a board JSON object into the sim's Board shape. */
export function parseBoard(data: BoardData): Board {
  const { id, width, height, rows } = data;
  if (!id) throw new Error('Board missing id');
  if (rows.length !== height) throw new Error(`Board ${id}: expected ${height} rows, got ${rows.length}`);
  const dug: number[] = new Array(width * height).fill(0);
  const gems: number[] = new Array(width * height).fill(0);
  const gemTypes: number[] = new Array(width * height).fill(0);
  const sacks: { x: number; y: number }[] = [];
  const boulders: { x: number; y: number }[] = [];
  const rowObstacles: ObstacleData[] = [];
  let playerStart: { x: number; y: number } | null = null;
  let spawn: { x: number; y: number } | null = null;
  for (let y = 0; y < height; y++) {
    const row = rows[y];
    if (row.length !== width) throw new Error(`Board ${id}: row ${y} has ${row.length} chars, expected ${width}`);
    for (let x = 0; x < width; x++) {
      const c = row[x];
      const i = y * width + x;
      switch (c) {
        case '#':
          break;
        case '.':
          dug[i] = 1;
          break;
        case '*':
          gems[i] = 1;
          gemTypes[i] = defaultGemType(x, y);
          break;
        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
          // Explicit gem type (WP5): 0 ruby, 1 emerald, 2 sapphire, 3 amethyst, 4 topaz.
          gems[i] = 1;
          gemTypes[i] = Number(c);
          break;
        case 'O':
          sacks.push({ x, y });
          break;
        case 'B':
          boulders.push({ x, y });
          break;
        case 'H':
          rowObstacles.push({ type: 'hardDirt', cell: [x, y] });
          break;
        case 'P':
          if (playerStart) throw new Error(`Board ${id}: multiple player starts`);
          playerStart = { x, y };
          dug[i] = 1;
          break;
        case 'S':
          if (spawn) throw new Error(`Board ${id}: multiple spawn cells`);
          spawn = { x, y };
          dug[i] = 1;
          break;
        default:
          throw new Error(`Board ${id}: unknown cell '${c}' at ${x},${y}`);
      }
    }
  }
  if (!playerStart) throw new Error(`Board ${id}: no player start (P)`);
  if (!spawn) throw new Error(`Board ${id}: no creature spawn (S)`);
  if (spawn.y !== 0 || spawn.x !== Math.floor(width / 2)) {
    throw new Error(`Board ${id}: spawn must be top-centre (${Math.floor(width / 2)},0), got ${spawn.x},${spawn.y}`);
  }
  const params = validateParams(data.params, id);
  for (const b of data.obstacles ?? []) if (b.type === 'boulder') boulders.push({ x: b.cell[0], y: b.cell[1] });
  if (data.gauntlet === true && (data.goals?.length ?? 0) > 0) throw new Error(`Board ${id}: gauntlet boards must not have goals`);
  const objectives = buildObjectives(data, [...rowObstacles, ...(data.obstacles ?? []).filter((o) => o.type !== 'boulder')], gems, gemTypes, dug, params);
  return {
    id,
    name: data.name ?? id,
    teaches: data.teaches ?? '',
    width,
    height,
    dug,
    gems,
    gemTypes,
    sacks,
    boulders,
    playerStart,
    spawn,
    params,
    hard: data.hard === true,
    superHard: data.superHard === true,
    gauntlet: data.gauntlet === true,
    objectives,
  };
}

function gemTypeIndex(v: unknown, boardId: string, where: string): number {
  if (v === undefined) return -1;
  if (typeof v === 'number') {
    if (!Number.isInteger(v) || v < 0 || v >= GEM_TYPE_COUNT) throw new Error(`Board ${boardId}: ${where} gemType ${v} out of range`);
    return v;
  }
  const i = (GEM_NAMES as readonly string[]).indexOf(String(v));
  if (i < 0) throw new Error(`Board ${boardId}: ${where} unknown gemType '${String(v)}'`);
  return i;
}

/** Build the objectives template (Systems Brief §1) from the optional board fields; validated so authoring errors fail at load. */
function buildObjectives(data: BoardData, obstacleData: ObstacleData[], gems: number[], gemTypes: number[], dug: number[], params: LevelParams): Objectives {
  const id = data.id;
  const { width, height } = data;
  const cellOk = (c: [number, number] | undefined, where: string): BoardCell => {
    if (!c || c.length !== 2 || !Number.isInteger(c[0]) || !Number.isInteger(c[1]) || c[0] < 0 || c[1] < 0 || c[0] >= width || c[1] >= height) {
      throw new Error(`Board ${id}: ${where} needs a cell [x, y] inside the board`);
    }
    return { x: c[0], y: c[1] };
  };
  const regions: Record<string, BoardCell[]> = {};
  for (const [key, r] of Object.entries(data.regions ?? {})) {
    const cells: BoardCell[] = [];
    if ('rect' in r) {
      const [rx, ry, rw, rh] = (r as { rect: [number, number, number, number] }).rect;
      for (let y = ry; y < ry + rh; y++) for (let x = rx; x < rx + rw; x++) cells.push(cellOk([x, y], `region ${key}`));
    } else {
      for (const c of (r as { cells: [number, number][] }).cells) cells.push(cellOk(c, `region ${key}`));
    }
    regions[key] = cells;
  }
  const goals: Goal[] = (data.goals ?? []).map((g, i) => {
    const gid = g.id ?? `goal${i + 1}`;
    const phase = g.phase ?? 0;
    const goal: Goal = { id: gid, type: g.type, phase, gemType: -1, target: 1, progress: 0, done: false, regionId: '', cell: null };
    switch (g.type) {
      case 'collect':
        goal.gemType = gemTypeIndex(g.gemType, id, `goal ${gid}`);
        goal.target = g.count ?? 0;
        break;
      case 'crush':
      case 'break':
        goal.target = g.count ?? 0;
        break;
      case 'survive':
        goal.target = Math.round(((g.seconds ?? 0) * 1000) / DEFAULTS.tickMs);
        break;
      case 'clear': {
        if (!g.regionId || !regions[g.regionId]) throw new Error(`Board ${id}: goal ${gid} needs a regionId that exists in regions`);
        goal.regionId = g.regionId;
        goal.target = regions[g.regionId].filter((c) => dug[c.y * width + c.x] === 0).length;
        break;
      }
      case 'rescue':
        goal.cell = cellOk(g.cell, `goal ${gid}`);
        goal.target = 1;
        break;
      default:
        throw new Error(`Board ${id}: goal ${gid} has unknown type '${String((g as { type: string }).type)}'`);
    }
    if (goal.target <= 0) throw new Error(`Board ${id}: goal ${gid} has an empty target`);
    return goal;
  });
  const phaseCount = goals.length === 0 ? 0 : Math.max(data.phases?.length ?? 0, ...goals.map((g) => g.phase + 1));
  for (let k = 0; k < phaseCount; k++) {
    if (!goals.some((g) => g.phase === k)) throw new Error(`Board ${id}: phase ${k} has no goals`);
  }
  const obstacles: Obstacle[] = obstacleData.map((o, i) => {
    const oid = o.id ?? `${o.type}${i + 1}`;
    const cell = cellOk(o.cell, `obstacle ${oid}`);
    const ob: Obstacle = { id: oid, type: o.type, x: cell.x, y: cell.y, phase: o.phase ?? 0, active: false, done: false, hits: 0, hitsLeft: 0, cracked: false, unlocked: false, switchCell: null, rateTicks: 0, timer: 0, gemType: -1, spawnGap: 0 };
    const i0 = cell.y * width + cell.x;
    switch (o.type) {
      case 'pillar':
        ob.hits = o.hits ?? 2;
        if (ob.hits < 1) throw new Error(`Board ${id}: pillar ${oid} needs hits >= 1`);
        ob.hitsLeft = ob.hits;
        break;
      case 'hardDirt':
        if (dug[i0] === 1) throw new Error(`Board ${id}: hard dirt ${oid} must be on a dirt cell`);
        ob.active = true;
        break;
      case 'lockedGem':
        ob.switchCell = cellOk(o.switchCell, `locked gem ${oid} switchCell`);
        ob.gemType = gemTypeIndex(o.gemType, id, `locked gem ${oid}`);
        if (ob.gemType < 0) ob.gemType = gems[i0] ? gemTypes[i0] : defaultGemType(cell.x, cell.y);
        gems[i0] = 1;
        gemTypes[i0] = ob.gemType;
        ob.active = true;
        break;
      case 'gemSpring':
        ob.rateTicks = o.rateTicks ?? 0;
        if (ob.rateTicks < 1) throw new Error(`Board ${id}: gem spring ${oid} needs rateTicks >= 1`);
        ob.gemType = gemTypeIndex(o.gemType, id, `gem spring ${oid}`);
        if (ob.gemType < 0) ob.gemType = defaultGemType(cell.x, cell.y);
        break;
      case 'nest':
        ob.spawnGap = o.spawnGap ?? 0;
        break;
      case 'boulder':
        break;
      default:
        throw new Error(`Board ${id}: obstacle ${oid} has unknown type '${String((o as { type: string }).type)}'`);
    }
    if (ob.phase >= Math.max(1, phaseCount)) throw new Error(`Board ${id}: obstacle ${oid} activates at phase ${ob.phase} but the board has ${phaseCount} phase(s)`);
    return ob;
  });
  const ids = new Set<string>();
  for (const o of obstacles) {
    if (ids.has(o.id)) throw new Error(`Board ${id}: duplicate obstacle id ${o.id}`);
    ids.add(o.id);
  }
  const pickups: Pickup[] = (data.pickups ?? []).map((pk, i) => {
    const cell = cellOk(pk.cell, `pickup ${i + 1}`);
    if (!pk.type) throw new Error(`Board ${id}: pickup ${i + 1} needs a type`);
    return { type: pk.type, x: cell.x, y: cell.y, phase: pk.phase ?? 0, active: false, collected: false };
  });
  // Static completability (B2; Level Generator Spec §7.5): counts must fit the board's resources.
  // Nests are alternate spawn points, not extra creatures, so the creature budget is totalCreatures.
  const sum = (type: Goal['type'], pred: (g: Goal) => boolean = () => true) => goals.filter((g) => g.type === type && pred(g)).reduce((n, g) => n + g.target, 0);
  const crush = sum('crush');
  if (crush > params.totalCreatures) throw new Error(`Board ${id}: crush goals need ${crush} creatures but totalCreatures is ${params.totalCreatures}`);
  const pillars = obstacles.filter((o) => o.type === 'pillar').length;
  const breaks = sum('break');
  if (breaks > pillars) throw new Error(`Board ${id}: break goals need ${breaks} pillars but the board has ${pillars}`);
  const springs = obstacles.filter((o) => o.type === 'gemSpring');
  const gemsOfType = (t: number) => gems.reduce((n, g, i) => n + (g && (t < 0 || gemTypes[i] === t) ? 1 : 0), 0);
  for (const t of [-1, ...Array.from({ length: GEM_TYPE_COUNT }, (_, k) => k)]) {
    if (springs.some((s) => t < 0 || s.gemType === t)) continue; // a spring makes the supply unbounded
    const need = t < 0 ? sum('collect') : sum('collect', (g) => g.gemType === t);
    const have = gemsOfType(t);
    if (need > have) throw new Error(`Board ${id}: collect goals need ${need} ${t < 0 ? 'gems' : GEM_NAMES[t] + ' gems'} but the board has ${have}`);
  }
  return { goals, phaseCount, phase: 0, phases: (data.phases ?? []).map((p) => ({ name: p.name, activations: p.activations ? [...p.activations] : undefined })), obstacles, regions, pickups, inventory: {}, nestTurn: false };
}
